<template>
    <div v-if="show">
        <div v-if="contentOnly">
            <router-view></router-view>
        </div>

        <template v-else>
            <top-menu></top-menu>
            <nav-menu></nav-menu>

            <div class="content">
                <router-view></router-view>
            </div>

            <impersonate-warning-modal ref="warningModal"></impersonate-warning-modal>

            <div id="app-footer" class="sm-12">
                <div class="t-center ">
                    {{ appName }} &copy; {{ appDate }}
                </div>
            </div>
        </template>

        <ks-modal
            v-if="show_entity_switch_modal"
            ref="entitySwitchedModal"
            :show-close="false"
            :show-header="false"
            :close-on-escape="false"
            :close-on-mask-click="false"
            max-width="450px"
        >
            <div class="bold">
                You have switched companies from a different tab.
            </div>
            <div class="mt-4 text-sm">
                Reload to switch to the new company here,
                or just wait while you make changes in the other tab.
            </div>
            <div class="mt-4 mb-0 alert warning text-sm">
                Note: Any pending changes will <strong>not</strong> be saved when reloading.
            </div>

            <template v-slot:footer>
                <vault-button @click="reloadTab">Reload</vault-button>
            </template>
        </ks-modal>
    </div>
</template>

<script>
    // External Dependencies
    import {mapActions, mapGetters} from 'vuex';

    // Internal Dependencies
    import { routeIsAuthorized } from './helpers/permissions';
    import NavMenu from './Vault/Navigation/NavMenu.vue';
    import TopMenu from './Vault/Navigation/TopMenu.vue';
    import EventHandler from './helpers/EventHandler';
    import IntegrationRepository from './Vault/Admin/DataIntegrations/repositories/IntegrationRepository';
    import UsersRepository from './Vault/Users/repositories/UsersRepository';
    import MentionsMixin from './Vault/UI/mixins/MentionsMixin.js';
    import StopImpersonationMixin from '@/mixins/StopImpersonationMixin';
    import api from 'kickstart-ui/lib/api';
    import {hasClass, removeClass} from '@Vault/Diagrams/diagram-helpers';

    export default {
        name: 'App',

        mixins: [
            MentionsMixin,
            StopImpersonationMixin,
        ],

        props: {
            entity: {
                type: Object,
                required: true,
            },
            userId: {},
            userRoleId: {},
            username: {},
            userDisplay: {},
            userType: {},
            channels: {
                required: true,
                type: Object
            },
            perms: {},
            userRole: {},
            systemAdmin: {
                default: false,
            },
            isImpersonating: {
                default: false,
            },
            appVersion: {
                type: String,
                default: import.meta.env.VITE_APP_VERSION,
            },
            appName: {
                type: String,
                default: import.meta.env.VITE_APP_NAME,
            },
            appType: {
                type: String,
                default: 'work',
            },
            appDate: {
                type: String,
            },
            hasTaskCategories: {
                default: false,
            },
        },

        computed: {
            ...mapGetters([
                'permissions',
                'app_version',
                'refresh_required',
                'is_impersonating',
                'switching_entities',
            ]),

            contentOnly() {
                return this.$route?.meta?.popup ?? false;
            },
        },

        data() {
            return {
                show: false,
                show_entity_switch_modal: false,
                block_navigation: null,
            };
        },

        created() {
            this.setEntityId(this.entity.entity_id);
            this.setEntity(this.entity);
            this.setUserInfo({
                user_id: this.userId,
                username: this.username,
                user_role_id: this.userRoleId,
                user_display: this.userDisplay,
                user_type: this.userType,
            });

            this.setPermissions(this.perms);
            this.checkPermissions();
            this.setUserRole(this.userRole);
            this.setSystemAdmin(this.systemAdmin);
            this.setIsImpersonating(this.isImpersonating);
            this.setAppVersion(this.appVersion);
            this.setHasTaskCategories(this.hasTaskCategories);
            this.setupEventChannels();
        },

        mounted() {
            this.setAppLoaded(true);
            this.setAppType(this.appType);

            this.fetchDataIntegrationOptions();

            EventHandler.channel('entity')
                .listen('refresh-integration-options', () => {
                    this.fetchDataIntegrationOptions();
                });

            EventHandler.channel('user')
                .listen('system-data-change', (data) => {
                    Object.values(data.types || []).forEach((type) => {
                        if (type == 'permission' || type == 'user_role') {
                            this.fetchUserAccessData();
                        }
                    });
                })
                .listen('deactivated', (data) => {
                    if ( data.user_status === 'deactivated' ) {
                        this.$toast({
                            type: 'error',
                            message: 'Your account has been deactivated and you will be logged out momentarily'
                        });
                        setTimeout(() => {
                            api.post('/logout', {}, { baseURL: '/' })
                                .then(({data}) => {
                                    window.location.href = '/';
                                })
                                .catch(({response}) => {
                                    console.error('DA:ERR: ', response);
                                })
                        }, 3000);
                    }
                })
                .listen('switch-entity', this.onSwitchEntity)
                .listen('user_mentioned', (mention) => {
                    this.updateMention(mention);
                })
                .listen('user_mention_deleted', (mention) => {
                    this.deleteMention(mention);
                });

            this.loadEntityConfigs(this.entity.entity_id);
            this.impersonateListener();

            this.mobileMenuEvents();

            this.loadUser(['entities'])
                .then(({data}) => {
                    this.setUserEntities(data.entities);
                });

            this.loadMentions('user', this.userId)
                .then(({data}) => {
                    this.setMentions(data);
                });
        },

        beforeDestroy() {
            this.mobileMenuEvents(true);
        },

        methods: {
            ...mapActions([
                'setEntity',
                'setEntityId',
                'setUserInfo',
                'setUserId',
                'setUserRoleId',
                'setUsername',
                'setAppLoaded',
                'setPermissions',
                'setUserRole',
                'setAppType',
                'setIntegrationOptions',
                'setIntegrationsLoaded',
                'setUserType',
                'setSystemAdmin',
                'setUserEntities',
                'setIsImpersonating',
                'setRefreshRequired',
                'setAppVersion',
                'setHasTaskCategories',
                'setS3Configs',
                'setMentions',
                'updateMention',
                'deleteMention',
            ]),

            ...mapActions('config', [
                'loadEntityConfigs'
            ]),

            onSwitchEntity({entity_id}) {
                // ignore message if the current instance (tab) initiated the entity switch
                if ( this.switching_entities ) {
                    return;
                }

                if ( entity_id === this.entity.entity_id ) {
                    // switching back to current instance entity, hide the modal and resume navigation
                    this.show_entity_switch_modal = false;

                    if ( this.block_navigation ) {
                        this.block_navigation(); // unregisters the nav guard
                        this.block_navigation = null;
                    }

                } else if ( !this.show_entity_switch_modal ) {
                    // switching away from current instance entity, show the modal and block navigation
                    this.show_entity_switch_modal = true;

                    this.$nextTick(() => {
                        this.$refs.entitySwitchedModal.open();
                    });

                    this.block_navigation = this.$router.beforeEach((to, from, next) => {
                        next(false);
                    });

                } else {
                    // switching to yet a different non-current instance entity, do nothing
                }

            },

            reloadTab() {
                window.location.href = '/app/dashboard';
            },

            setupEventChannels() {
                for (let label in this.channels) {
                    // Create the channel.
                    EventHandler.addChannel(label, this.channels[label]);

                    // Each channel has 3 default events we listen on.
                    let channel = EventHandler.channel(label);

                    channel.listen('general-error', (message) => {
                        this.$toast({
                            type: 'error',
                            message: message.message || 'An error occurred',
                            duration: message.duration || 0,
                        });
                    }).listen('general-warning', (message) => {
                        if ( message.hasOwnProperty('message') ) {
                            this.$toast({
                                type: 'warning',
                                message: message.message,
                                duration: message.duration || 0,
                            });
                        }
                    }).listen('general-notice', (message) => {
                        if ( message.hasOwnProperty('message') ) {
                            this.$toast({
                                type: 'info',
                                message: message.message,
                                duration: message.duration || 0,
                            });
                        }
                    });
                }
            },

            checkPermissions() {
                this.$nextTick(() => {
                    if ( !routeIsAuthorized(this.$route) ) {
                        this.$router.replace({name: 'not-authorized'});
                    }

                    this.show = true;
                });
            },

            fetchDataIntegrationOptions() {
                return IntegrationRepository.list()
                    .then(({data}) => {
                        for ( const {integration_code, options, rules} of data ) {
                            this.setIntegrationOptions({integration_code, options, rules});
                        }

                    })
                    .catch(() => {
                        console.error('Failed to fetch data integration options.')
                    })
                    .finally(() => {
                        this.setIntegrationsLoaded(true);
                    });
            },

            transformPermissionsObjectToArray(permissions) {
                return Object.fromEntries(Object.values(permissions || []).map((permission) => {
                    return [permission.permission_code, permission.permission_id];
                }));
            },

            loadUser(withs = []) {
                return UsersRepository.find(this.userId, { with: withs.join(',') });
            },

            fetchUserAccessData() {
                this.loadUser(['permissions'])
                    .then(({data}) => {
                        this.setUserRole(data.role.role_name);
                        this.setUserType(data.role.role_type);
                        this.setAppType(data.role.app_type || 'work');
                        this.setPermissions(this.transformPermissionsObjectToArray(data.permissions));
                        this.setRefreshRequired();
                        this.$toast({
                            message: 'System data has changed. You may need to reload the app.',
                            type: 'warning'
                        });
                    })
                    .catch(() => {
                        this.$toast({
                            message: 'Unable to update system data. Please reload the app.',
                            type: 'error'
                        });
                    });
            },

            mobileMenuEvents(remove = false) {
                const top_nav_link = document.querySelector('.mobile-menu.has-sidebar');
                const side_nav_link = document.querySelector('.mobile-menu');
                const side_nav = document.querySelector('.side-nav');

                const handleTopNavClick = () => {
                    if ( hasClass(side_nav, 'show') ) {
                        removeClass(side_nav, 'show');
                        removeClass(document.body, 'side-nav-open');
                    }
                }
                const handleSideNavClick = () => {
                    if ( hasClass(top_nav_link.parentNode, 'show') ) {
                        removeClass(top_nav_link.parentNode, 'show');
                    }
                }

                if ( remove ) {
                    top_nav_link?.removeEventListener('click', handleTopNavClick);
                    side_nav_link?.removeEventListener('click', handleSideNavClick);
                } else {
                    top_nav_link?.addEventListener('click', handleTopNavClick);
                    side_nav_link?.addEventListener('click', handleSideNavClick);
                }
            },
        },

        watch: {
            permissions() {
                this.checkPermissions();
            },
        },

        components: {
            NavMenu,
            TopMenu,
        }
    }
</script>
